import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import './App.css';
import Overview from './pages/Overview';
import QuickStart from './pages/QuickStart';
import UserRoles from './pages/UserRoles';
import DashboardGuide from './pages/DashboardGuide';
import SigningFlow from './pages/SigningFlow';
import Verification from './pages/Verification';
import Branding from './pages/Branding';
import APIKeys from './pages/APIKeys';
import APIReference from './pages/APIReference';
import APIEndpoints from './data/apiEndpoints';

// Documentation always uses default yellow/black theme; dashboard settings do not affect this tab.
const DOCS_THEME = 'dark';

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', DOCS_THEME);
  }, []);

  return (
    <Router>
      <div className="docs-app" data-theme={DOCS_THEME}>
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        <div className="docs-layout">
          <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          <main className="docs-main">
            <DocsContentTabs />
            <div className="docs-main-content">
            <Routes>
              <Route path="/" element={<Overview />} />
              <Route path="/quick-start" element={<QuickStart />} />
              <Route path="/user-roles" element={<UserRoles />} />
              <Route path="/dashboard" element={<DashboardGuide />} />
              <Route path="/signing-flow" element={<SigningFlow />} />
              <Route path="/verification" element={<Verification />} />
              <Route path="/branding" element={<Branding />} />
              <Route path="/api-keys" element={<APIKeys />} />
              <Route path="/api-reference" element={<APIReference />} />
              <Route path="/api-reference/:section" element={<APIReference />} />
            </Routes>
            </div>
          </main>
        </div>
      </div>
    </Router>
  );
}

function Header({ onMenuClick }) {
  return (
    <header className="docs-header">
      <div className="docs-header-content">
        <div className="docs-header-brand">
          <button className="docs-menu-btn" onClick={onMenuClick} aria-label="Open menu">
            <FiMenu size={20} />
          </button>
          <Link to="/" className="docs-header-logo">
            <span className="docs-header-logo-sign">Sign</span>
            <span className="docs-header-logo-tada">Tada</span>
          </Link>
        </div>
      </div>
    </header>
  );
}

function DocsContentTabs() {
  const location = useLocation();
  const isAPIReference = location.pathname.startsWith('/api-reference');
  return (
    <nav className="docs-content-tabs" aria-label="Documentation sections">
      <Link to="/" className={`docs-content-tab ${!isAPIReference ? 'active' : ''}`}>
        Documentation
      </Link>
      <Link to="/api-reference" className={`docs-content-tab ${isAPIReference ? 'active' : ''}`}>
        API Reference
      </Link>
    </nav>
  );
}

const API_SECTION_ORDER = ['auth', 'requests', 'templates', 'webhooks', 'files', 'signing', 'users', 'otp', 'branding'];

function getEndpointLabel(endpoint) {
  const method = endpoint.method.toUpperCase();
  const path = endpoint.path.replace(/^\/[^/]+\//, '').replace(/\/<[^>]+>/g, '');
  const name = path.split('/').filter(Boolean).pop() || path || 'Endpoint';
  const label = name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return `${method} ${label}`;
}

function Sidebar({ isOpen, onClose }) {
  const location = useLocation();
  const pathname = location.pathname;
  const hash = location.hash || '';
  const apiSectionMatch = pathname.match(/^\/api-reference\/([^/]+)$/);
  const currentApiSection = apiSectionMatch ? apiSectionMatch[1] : null;
  const currentSectionData = currentApiSection ? APIEndpoints[currentApiSection] : null;

  const sections = [
    {
      title: 'Getting Started',
      items: [
        { path: '/', label: 'Overview' },
        { path: '/quick-start', label: 'Quick Start' },
        { path: '/user-roles', label: 'User Roles' },
        { path: '/dashboard', label: 'Dashboard Guide' },
        { path: '/signing-flow', label: 'Signing Flow' },
        { path: '/verification', label: 'Document Verification' },
        { path: '/branding', label: 'Branding' },
        { path: '/api-keys', label: 'API Keys' },
      ],
    },
    {
      title: 'API Reference',
      items: [
        { path: '/api-reference', label: 'Overview' },
        ...API_SECTION_ORDER.filter(k => APIEndpoints[k]).map(sectionKey => ({
          path: `/api-reference/${sectionKey}`,
          label: APIEndpoints[sectionKey].title,
          sectionKey,
          endpoints: APIEndpoints[sectionKey].endpoints,
        })),
      ],
    },
  ];

  return (
    <>
      {isOpen && <div className="docs-sidebar-overlay" onClick={onClose} />}
      <aside className={`docs-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="docs-sidebar-search">
          <div className="docs-sidebar-search-wrap">
            <span className="docs-sidebar-search-kbd" aria-hidden>⌘</span>
            <input type="text" placeholder="Search... Ctrl+K" readOnly aria-label="Search documentation" />
          </div>
        </div>
        <div className="docs-sidebar-header">
          <span className="docs-sidebar-title">Navigation</span>
          <button className="docs-sidebar-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        <nav className="docs-sidebar-nav">
          {sections.map((section, idx) => (
            <div key={idx} className="docs-sidebar-section">
              <div className="docs-sidebar-section-title">{section.title}</div>
              {section.items.map((item, itemIdx) => {
                const isActive = pathname === item.path;
                const showEndpoints = item.sectionKey && currentApiSection === item.sectionKey && item.endpoints;
                return (
                  <div key={itemIdx} className="docs-sidebar-endpoint-group">
                    <Link
                      to={item.path}
                      className={`docs-sidebar-link ${isActive ? 'active' : ''}`}
                      onClick={onClose}
                    >
                      {item.label}
                    </Link>
                    {showEndpoints && (
                      <div className="docs-sidebar-endpoints">
                        {item.endpoints.map((ep, epIdx) => {
                          const isEndpointActive = pathname === item.path && hash === `#endpoint-${epIdx}`;
                          return (
                            <a
                              key={epIdx}
                              href={`${item.path}#endpoint-${epIdx}`}
                              className={`docs-sidebar-link docs-sidebar-sublink ${isEndpointActive ? 'active' : ''}`}
                              onClick={onClose}
                            >
                              <span className={`docs-sidebar-method docs-method-${(ep.method || 'get').toLowerCase()}`}>
                                {ep.method.toUpperCase()}
                              </span>
                              {getEndpointLabel(ep)}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

export default App;
